/**
 * Locks body scroll while a fullscreen/modal overlay is open, and optionally
 * requests real OS/browser-chrome-hiding fullscreen for immersive viewers
 * (image slideshow, gear detail popup).
 *
 * `overflow: hidden` alone is not a real scroll lock on mobile: touch-scroll
 * gestures still nudge the page a few pixels, which is exactly what re-shows
 * the browser's address bar mid-overlay. So instead the body is pinned with
 * `position: fixed` at its current scroll offset (the classic iOS-safe
 * scroll-lock recipe) and restored to that offset on unlock.
 */
let locked = false;
let savedScrollY = 0;

// Guarded against re-entrant calls: Component.setState() re-fires onMounted()
// on every re-render (e.g. the slideshow's prev/next), which would otherwise
// call lockScroll() again while already locked — and since the page reads as
// scrolled to 0 while pinned, that would clobber the real saved position.
export function lockScroll() {
  if (locked) return;
  locked = true;
  savedScrollY = window.scrollY || window.pageYOffset || 0;
  document.body.style.top = `-${savedScrollY}px`;
  document.body.classList.add("scroll-locked");
  // Mobile browsers only resolve their dynamic viewport (collapse the
  // address bar) after a scroll gesture, so a 100dvh overlay opened without
  // this nudge can be left with a gap where the still-expanded bar was.
  requestAnimationFrame(() => window.scrollTo(0, 1));
}

export function unlockScroll() {
  if (!locked) return;
  locked = false;
  document.body.classList.remove("scroll-locked");
  document.body.style.top = "";
  window.scrollTo(0, savedScrollY);
}

/**
 * True fullscreen (hides the OS status bar / browser chrome, not just sizes
 * around it) — reserved for actual full-bleed media viewers, not form
 * modals. Support is inconsistent (notably absent on iPhone Safari for
 * arbitrary elements) and it requires an active user gesture, so this is a
 * best-effort progressive enhancement: it silently no-ops on failure and the
 * dvh + scroll-lock fallback above still applies either way.
 */
export async function enterFullscreen(el) {
  const request = el.requestFullscreen || el.webkitRequestFullscreen;
  try {
    await request?.call(el);
  } catch {
    // Unsupported or blocked (no user gesture, iOS Safari, etc.) — ignore.
  }
}

export function exitFullscreen() {
  if (document.fullscreenElement) {
    document.exitFullscreen?.().catch(() => {});
  } else if (document.webkitFullscreenElement) {
    document.webkitExitFullscreen?.();
  }
}
